/**
 * CORE-G / CORE-AB — reading back the efficiency basis a placement actually used.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY A SNAPSHOT AND NOT A LOOKUP
 * ═══════════════════════════════════════════════════════════════════════════
 * `assignment_audit` records `efficiency_score` and has no column for what that
 * number rested on, so an audit read a year later could not tell a 125 learned
 * from forty visits from a 125 nobody classified from an invented 100.
 *
 * `smart-assign` now writes the resolved basis into `explainability_details`,
 * the durable JSONB snapshot the table already has — no migration, and the
 * smallest thing that closes the gap.
 *
 * It is taken from the SAME resolved object the ranker planned with, and
 * deliberately not re-resolved from the technician row. `technicians` is
 * mutable: a manager correcting an efficiency, or CORE-AN learning a new one,
 * must not be able to change what a completed assignment says it used. An audit
 * record that re-reads live state is not an audit record.
 *
 * This module only READS that snapshot. It resolves nothing, and where the
 * snapshot is absent — every row written before this landed — it says so rather
 * than filling the gap from anywhere.
 */
import { isProvisionalLearnedSample } from '@/utils/efficiencyDisclosure';

/** The four classes, as `_shared/efficiencyResolution.ts` writes them. */
export type AuditEfficiencyBasis = 'derived' | 'asserted' | 'imported' | 'unrecorded';

/** The snapshot as it sits in `explainability_details.efficiency`. */
export interface EfficiencyAuditSnapshot {
  value: number | null;
  basis: AuditEfficiencyBasis | null;
  sample_size: number | null;
  is_established: boolean | null;
  is_fallback: boolean | null;
  /** R4 CORE-BB (decision 22b) — written by smart-assign from R4 on; absent on older rows. */
  is_provisional?: boolean | null;
}

export interface EfficiencyAuditReading {
  /** Short label for the audit row. */
  label: string;
  /** The full sentence. */
  detail: string;
  /** Null when the snapshot predates this field — a real state, not an error. */
  basis: AuditEfficiencyBasis | null;
  /** True only where the value was established from completed work. */
  isEstablished: boolean;
  /** True only where the number itself was invented. */
  isFallback: boolean;
  /** R4: a learned value still inside the algorithm's early-learning band when the placement was made. */
  isProvisional?: boolean;
}

const isBasis = (v: unknown): v is AuditEfficiencyBasis =>
  v === 'derived' || v === 'asserted' || v === 'imported' || v === 'unrecorded';

const numOrNull = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

/**
 * Pull the snapshot out of an `explainability_details` blob.
 *
 * Returns null for anything that is not a recognisable snapshot, including the
 * rows written before this field existed. Those are not broken records — they
 * are records from before the question could be answered, and the reader says
 * that rather than inventing a class for them.
 */
export function readEfficiencyAuditSnapshot(
  details: unknown,
): EfficiencyAuditSnapshot | null {
  if (!details || typeof details !== 'object') return null;
  const raw = (details as Record<string, unknown>).efficiency;
  if (!raw || typeof raw !== 'object') return null;

  const r = raw as Record<string, unknown>;
  const basis = isBasis(r.basis) ? r.basis : null;
  const value = numOrNull(r.value);

  // A blob with neither a class nor a number is not a snapshot of anything.
  if (basis === null && value === null) return null;

  return {
    value,
    basis,
    sample_size: numOrNull(r.sample_size),
    is_established: typeof r.is_established === 'boolean' ? r.is_established : null,
    is_fallback: typeof r.is_fallback === 'boolean' ? r.is_fallback : null,
    is_provisional: typeof r.is_provisional === 'boolean' ? r.is_provisional : null,
  };
}

/**
 * Describe what the placement's efficiency rested on.
 *
 * The `unrecorded` class splits in two on `is_fallback`, because they are
 * different facts: a technician's own recorded number that nobody labelled, and
 * a number that was invented because there was none. Both are unclassified;
 * only the second is made up.
 */
export function describeEfficiencyAudit(
  snapshot: EfficiencyAuditSnapshot | null,
): EfficiencyAuditReading {
  if (!snapshot) {
    return {
      label: 'Not recorded',
      detail:
        'This placement predates the efficiency basis being recorded, so what its ' +
        'timing rested on cannot be reconstructed.',
      basis: null,
      isEstablished: false,
      isFallback: false,
    };
  }

  const pct = snapshot.value === null ? null : `${Math.round(snapshot.value)}%`;

  switch (snapshot.basis) {
    case 'derived': {
      const n = snapshot.sample_size;
      // R4 (decision 22b): the record's own flag when it has one; otherwise the record's OWN sample size decides —
      // what the value rested on at the time, never the technician's value today.
      const provisional = typeof snapshot.is_provisional === 'boolean'
        ? snapshot.is_provisional
        : isProvisionalLearnedSample(n);
      return {
        // OD-35 (2026-10-15): the same words as the live disclosure (efficiencyDisclosure.ts).
        label: provisional ? 'Calculated by OptiBay · provisional' : 'Calculated by OptiBay',
        detail:
          (n && n > 0
            ? `Planned with ${pct ?? 'a derived efficiency'}, learned from ${n} completed ${n === 1 ? 'visit' : 'visits'}.`
            : `Planned with ${pct ?? 'a derived efficiency'}. Its sample basis was not recorded.`) +
          (provisional ? ' Provisional: still inside the early-learning visits, where each visit moves it strongly.' : ''),
        basis: 'derived',
        isEstablished: !provisional,
        isFallback: false,
        isProvisional: provisional,
      };
    }
    case 'asserted':
      return {
        label: 'Set by a person',
        detail: `Planned with ${pct ?? 'an efficiency'} that a person stated, not one derived from completed work.`,
        basis: 'asserted',
        isEstablished: false,
        isFallback: false,
      };
    case 'imported':
      return {
        label: 'Reported by External Source',
        detail: `Planned with ${pct ?? 'an efficiency'} imported from another system, not derived from work done here.`,
        basis: 'imported',
        isEstablished: false,
        isFallback: false,
      };
    case 'unrecorded':
    default: {
      if (snapshot.is_fallback) {
        return {
          label: 'Fallback',
          detail: `No efficiency was recorded for this technician, so planning used ${pct ?? '100%'} as a fallback.`,
          basis: 'unrecorded',
          isEstablished: false,
          isFallback: true,
        };
      }
      return {
        label: 'Unclassified',
        detail: `Planned with the technician’s recorded ${pct ?? 'efficiency'}. Nobody recorded where it came from.`,
        basis: 'unrecorded',
        isEstablished: false,
        isFallback: false,
      };
    }
  }
}
